import { createFileRoute } from "@tanstack/react-router";
import { AlertTriangle, Wallet } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { PaymentMethods } from "@/components/billing/PaymentMethods";
import { TopUpDialog } from "@/components/billing/TopUpDialog";
import { UsageHistory } from "@/components/billing/UsageHistory";
import { WalletOverviewCard } from "@/components/billing/WalletOverviewCard";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useWalletQuery } from "@/hooks/use-billing";
import { useSession } from "@/hooks/use-session";
import { trackBillingViewed } from "@/lib/analytics-events";
import type { WalletResponse } from "@/types/billing";

export const Route = createFileRoute("/_app/_settings/billing")({
	component: BillingPage,
});

function BillingPage() {
	const session = useSession();
	const campaignId = session?.campaign?.id ?? 0;
	const {
		data: wallet,
		isLoading,
		isError,
		refetch,
	} = useWalletQuery(campaignId);

	// BILL-08 analytics: fire once per successful wallet load.
	const trackedRef = useRef(false);
	useEffect(() => {
		if (wallet && !trackedRef.current) {
			trackedRef.current = true;
			trackBillingViewed(wallet.tier, wallet.balance_micros);
		}
	}, [wallet]);

	return (
		<div className="space-y-8 px-6 py-6">
			{isLoading ? (
				<WalletLoadingSkeleton />
			) : isError ? (
				<WalletErrorState onRetry={() => refetch()} />
			) : !wallet ? (
				<WalletErrorState onRetry={() => refetch()} />
			) : wallet.tier === "new" ? (
				<NewWalletEmptyState wallet={wallet} campaignId={campaignId} />
			) : (
				<WalletOverviewCard wallet={wallet} />
			)}

			{/* Payment methods (BILL-08) — manage saved cards independently. */}
			{!isLoading && !isError && wallet && (
				<PaymentMethods campaignId={campaignId} />
			)}

			{/* Usage history loads independently — visible for funded wallets. */}
			{!isLoading && !isError && wallet && wallet.tier !== "new" && (
				<UsageHistory campaignId={campaignId} />
			)}
		</div>
	);
}

/** BILL-01d — loading skeleton. */
function WalletLoadingSkeleton() {
	return (
		<Card>
			<CardHeader>
				<Skeleton className="h-5 w-32" />
				<Skeleton className="mt-3 h-12 w-48" />
				<Skeleton className="mt-2 h-4 w-64" />
			</CardHeader>
			<CardContent className="space-y-5">
				<div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
					{["a", "b", "c", "d"].map((k) => (
						<Skeleton key={k} className="h-14 w-full" />
					))}
				</div>
				<Skeleton className="h-20 w-full" />
				<Skeleton className="h-40 w-full" />
			</CardContent>
		</Card>
	);
}

/** BILL-01e — error state with retry. */
function WalletErrorState({ onRetry }: { onRetry: () => void }) {
	return (
		<Card>
			<CardContent className="flex flex-col items-center gap-4 py-16 text-center">
				<AlertTriangle className="size-10 text-destructive" />
				<div>
					<p className="text-base font-medium">Couldn't load your balance</p>
					<p className="mt-1 text-sm text-muted-foreground">
						Something went wrong fetching your credit wallet. Please try again.
					</p>
				</div>
				<Button variant="outline" onClick={onRetry}>
					Retry
				</Button>
			</CardContent>
		</Card>
	);
}

/** BILL-01f — new-account empty state (never funded). */
function NewWalletEmptyState({
	wallet,
	campaignId,
}: {
	wallet: WalletResponse;
	campaignId: number;
}) {
	const [topUpOpen, setTopUpOpen] = useState(false);
	return (
		<Card>
			<CardHeader>
				<CardTitle>Billing</CardTitle>
			</CardHeader>
			<CardContent className="flex flex-col items-center gap-4 py-16 text-center">
				<Wallet className="size-10 text-muted-foreground/60" />
				<div>
					<p className="text-base font-medium">Add your first credits</p>
					<p className="mt-1 max-w-md text-sm text-muted-foreground">
						Prepay for text and phone banking. You'll only spend against your
						balance — email is always free.
					</p>
				</div>
				<Button onClick={() => setTopUpOpen(true)}>Add credits</Button>
			</CardContent>
			<TopUpDialog
				open={topUpOpen}
				onOpenChange={setTopUpOpen}
				wallet={wallet}
				campaignId={campaignId}
			/>
		</Card>
	);
}
